import React, { useState, useRef, useCallback, useEffect } from 'react';
import type { Lead } from '../../types';
import type { HoverPreviewState, TableColumn } from '../../types/table';
import { TABLE_TIMINGS, HOVER_PREVIEW } from '../../constants/table';
import { isMatchBadge } from '../../utils/tableUtils';

const initialState: HoverPreviewState = {
  show: false,
  content: '',
  x: 0,
  y: 0,
  maxWidth: 0,
};

export interface UseHoverPreviewProps {
  columns: TableColumn[];
}

export interface UseHoverPreviewReturn {
  hoverPreview: HoverPreviewState;
  hoverPreviewRef: React.RefObject<HTMLDivElement | null>;
  isMouseInPreview: React.MutableRefObject<boolean>;
  handleHeaderHoverPreview: (e: React.MouseEvent, columnKey: string, columnName: string) => void;
  handleHeaderHoverPreviewLeave: () => void;
  handleCellHoverPreview: (e: React.MouseEvent, content: string, isEmail?: boolean, leadData?: Lead) => void;
  handleCellHoverPreviewLeave: () => void;
  closeHoverPreview: () => void;
  hideOnClick: () => void;
}

/**
 * Hook for managing hover preview tooltips for truncated cells and headers
 */
export const useHoverPreview = ({
  columns,
}: UseHoverPreviewProps): UseHoverPreviewReturn => {
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewState>(initialState);

  const hoverPreviewRef = useRef<HTMLDivElement | null>(null);
  const hoverPreviewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMouseInPreview = useRef(false);
  const currentHoverTarget = useRef<HTMLElement | null>(null);
  const currentHoverCellId = useRef<string | null>(null);

  /**
   * Close hover preview and clear all refs/timeouts
   */
  const closeHoverPreview = useCallback(() => {
    setHoverPreview(initialState);
    isMouseInPreview.current = false;
    currentHoverTarget.current = null;
    currentHoverCellId.current = null;
    if (hoverPreviewTimeoutRef.current) {
      clearTimeout(hoverPreviewTimeoutRef.current);
      hoverPreviewTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  /**
   * Hide preview on cell click
   */
  const hideOnClick = useCallback(() => {
    setHoverPreview(initialState);
    currentHoverTarget.current = null;
    currentHoverCellId.current = null;
    if (hoverPreviewTimeoutRef.current) {
      clearTimeout(hoverPreviewTimeoutRef.current);
      hoverPreviewTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  /**
   * Calculate position for hover preview
   */
  const calculatePosition = useCallback((rect: DOMRect) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportMargin = HOVER_PREVIEW.VIEWPORT_MARGIN;
    const maxWidth = Math.min(HOVER_PREVIEW.MAX_WIDTH_PX, viewportWidth * HOVER_PREVIEW.MAX_WIDTH_RATIO);
    const estimatedPreviewHeight = HOVER_PREVIEW.ESTIMATED_HEIGHT;

    // Default anchor: bottom-left
    let x = rect.left;
    let y = rect.bottom + HOVER_PREVIEW.GAP;

    // Flip logic: if not enough space below, flip above
    if (y + estimatedPreviewHeight > viewportHeight - viewportMargin) {
      y = rect.top - estimatedPreviewHeight - HOVER_PREVIEW.GAP;
    }

    // Horizontal clamping: shift left if overflowing right edge
    if (x + maxWidth > viewportWidth - viewportMargin) {
      x = Math.max(viewportMargin, viewportWidth - maxWidth - viewportMargin);
    } else {
      x = Math.max(viewportMargin, x);
    }

    // Vertical clamping
    y = Math.max(viewportMargin, y);

    return { x, y, maxWidth };
  }, []);

  /**
   * Handle header hover preview
   */
  const handleHeaderHoverPreview = useCallback((
    e: React.MouseEvent,
    columnKey: string,
    columnName: string
  ) => {
    // Skip Match/status columns only
    if (isMatchBadge(columnName)) {
      return;
    }

    const headerElement = e.currentTarget as HTMLElement;
    const headerId = `header:${columnKey}`;

    // If we're already hovering this exact header, don't retrigger
    if (currentHoverCellId.current === headerId && hoverPreview.show) {
      return;
    }

    // Cancel any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Debounce: cancel previous timeout
    if (hoverPreviewTimeoutRef.current) {
      clearTimeout(hoverPreviewTimeoutRef.current);
    }

    // Track this as the current hover target
    currentHoverTarget.current = headerElement;
    currentHoverCellId.current = headerId;

    // Check truncation after a delay
    hoverPreviewTimeoutRef.current = setTimeout(() => {
      const rect = headerElement.getBoundingClientRect();
      const { x, y, maxWidth } = calculatePosition(rect);

      // Get column metadata
      const column = columns.find(col => col.key === columnKey);
      const typeLabel = column?.typeLabel;
      const description = column?.description;

      setHoverPreview({
        show: true,
        content: columnName,
        title: columnName,
        dataType: typeLabel,
        description: description,
        x,
        y,
        maxWidth,
        isHeader: true,
      });
    }, TABLE_TIMINGS.HOVER_PREVIEW_DEBOUNCE);
  }, [columns, hoverPreview.show, calculatePosition]);

  /**
   * Handle header hover leave
   */
  const handleHeaderHoverPreviewLeave = useCallback(() => {
    // Cancel pending show
    if (hoverPreviewTimeoutRef.current) {
      clearTimeout(hoverPreviewTimeoutRef.current);
      hoverPreviewTimeoutRef.current = null;
    }

    // Cancel any existing hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // Delay hiding to allow mouse to move into preview card
    hideTimeoutRef.current = setTimeout(() => {
      if (!isMouseInPreview.current) {
        setHoverPreview(initialState);
        currentHoverTarget.current = null;
        currentHoverCellId.current = null;
      }
    }, TABLE_TIMINGS.HOVER_PREVIEW_HIDE_DELAY);
  }, []);

  /**
   * Handle cell hover preview (for truncated content)
   */
  const handleCellHoverPreview = useCallback((
    e: React.MouseEvent,
    content: string,
    isEmail: boolean = false,
    leadData?: Lead
  ) => {
    const cellElement = e.currentTarget as HTMLElement;
    const cellId = cellElement.getAttribute('data-cell-id') || `cell:${content.slice(0, 20)}`;

    // If we're already hovering this exact cell, don't retrigger
    if (currentHoverCellId.current === cellId && hoverPreview.show) {
      return;
    }

    // Cancel any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Debounce: cancel previous timeout
    if (hoverPreviewTimeoutRef.current) {
      clearTimeout(hoverPreviewTimeoutRef.current);
    }

    // Track this as the current hover target
    currentHoverTarget.current = cellElement;
    currentHoverCellId.current = cellId;

    hoverPreviewTimeoutRef.current = setTimeout(() => {
      const rect = cellElement.getBoundingClientRect();
      const { x, y, maxWidth } = calculatePosition(rect);

      setHoverPreview({
        show: true,
        content,
        x,
        y,
        maxWidth,
        isHeader: false,
        isEmail,
        leadData: leadData || null,
      });
    }, TABLE_TIMINGS.HOVER_PREVIEW_DEBOUNCE);
  }, [hoverPreview.show, calculatePosition]);

  /**
   * Handle cell hover leave
   */
  const handleCellHoverPreviewLeave = useCallback(() => {
    // Cancel pending show
    if (hoverPreviewTimeoutRef.current) {
      clearTimeout(hoverPreviewTimeoutRef.current);
      hoverPreviewTimeoutRef.current = null;
    }

    // Cancel any existing hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // Delay hiding to allow mouse to move into preview card
    hideTimeoutRef.current = setTimeout(() => {
      if (!isMouseInPreview.current) {
        setHoverPreview(initialState);
        currentHoverTarget.current = null;
        currentHoverCellId.current = null;
      }
    }, TABLE_TIMINGS.HOVER_PREVIEW_HIDE_DELAY);
  }, []);

  // Close hover preview on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (hoverPreview.show) {
        closeHoverPreview();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [hoverPreview.show, closeHoverPreview]);

  // Close hover preview on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && hoverPreview.show) {
        closeHoverPreview();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hoverPreview.show, closeHoverPreview]);

  return {
    hoverPreview,
    hoverPreviewRef,
    isMouseInPreview,
    handleHeaderHoverPreview,
    handleHeaderHoverPreviewLeave,
    handleCellHoverPreview,
    handleCellHoverPreviewLeave,
    closeHoverPreview,
    hideOnClick,
  };
};
